import React from 'react'

import { type OnboardingInfo } from 'components/member_onboarding_v2/types'
import { listToProseString } from 'components/members/utils'
import { type MemberOnboardingMemberPlanChangesProps } from 'utils/web_api.schemas'

import RenewalsConfirmComparisonTable from '../RenewalsConfirmComparisonTable/RenewalsConfirmComparisonTable'

interface CoveredParticipantsComparisonTableProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  onboardingData: OnboardingInfo;
}

export const getSubtitle = ({
  currentYearCoveredParticipants,
  nextYearCoveredParticipants,
  nextYear,
}: {
  currentYearCoveredParticipants: string[];
  nextYearCoveredParticipants: string[];
  nextYear: number;
}): string => {
  // TODO: make sure that this component acts the same as how enrollment comparator handles the difference
  // (https://github.com/thatch-health/thatch/blob/main/app/services/enrollments/enrollment_comparator.rb#L53)
  const addedParticipants = nextYearCoveredParticipants.filter(
    (p) => !currentYearCoveredParticipants.includes(p)
  )
  const removedParticipants = currentYearCoveredParticipants.filter(
    (p) => !nextYearCoveredParticipants.includes(p)
  )

  let subtitle = ''

  if (addedParticipants.length > 0 && removedParticipants.length > 0) {
    subtitle = `${listToProseString(
      addedParticipants
    )} will be added and ${listToProseString(
      removedParticipants
    )} will be removed, starting January 1, ${nextYear}`
  } else if (addedParticipants.length > 0) {
    subtitle = `${listToProseString(
      addedParticipants
    )} will be added starting January 1, ${nextYear}`
  } else if (removedParticipants.length > 0) {
    subtitle = `${listToProseString(
      removedParticipants
    )} will be removed after December 31`
  } else {
    subtitle = "You'll have the same people on your plan as you did this year"
  }

  return subtitle
}

export const getPrevParticipantsWithMetadata = ({
  currentYearCoveredParticipants,
  nextYearCoveredParticipants,
}: {
  currentYearCoveredParticipants: string[];
  nextYearCoveredParticipants: string[];
}) => {
  return currentYearCoveredParticipants.map((participant) => ({
    value: participant,
    status: !nextYearCoveredParticipants.includes(participant)
      ? ('removed_or_changed' as const)
      : ('unchanged' as const),
  }))
}

export const getNextParticipantsWithMetadata = ({
  currentYearCoveredParticipants,
  nextYearCoveredParticipants,
}: {
  currentYearCoveredParticipants: string[];
  nextYearCoveredParticipants: string[];
}) => {
  return nextYearCoveredParticipants.map((participant) => ({
    value: participant,
    status: !currentYearCoveredParticipants.includes(participant)
      ? ('added_or_changed' as const)
      : ('unchanged' as const),
  }))
}

export const CoveredParticipantsComparisonTable: React.FC<
  CoveredParticipantsComparisonTableProps
> = ({ planChangeSummary, onboardingData }) => {
  const currentPlanYear = planChangeSummary.current_plan_year?.plan_year
  const nextPlanYear = planChangeSummary.next_plan_year?.plan_year

  const currentYearCoveredParticipants =
    planChangeSummary?.current_plan_year?.covered_participants?.map(
      (p: any) => {
        return `${p.given_name} ${p.family_name}`
      }
    ) || []

  const nextYearCoveredParticipants = [
    onboardingData?.member?.given_name +
      ' ' +
      onboardingData?.member?.family_name,
  ].concat(
    onboardingData?.additional_participants?.map((p: any) => {
      return `${p.given_name} ${p.family_name}`
    }) || []
  )

  const subtitle = getSubtitle({
    currentYearCoveredParticipants,
    nextYearCoveredParticipants,
    nextYear: nextPlanYear,
  })

  const prevValuesWithMetadata = getPrevParticipantsWithMetadata({
    currentYearCoveredParticipants,
    nextYearCoveredParticipants,
  })

  const currValuesWithMetadata = getNextParticipantsWithMetadata({
    currentYearCoveredParticipants,
    nextYearCoveredParticipants,
  })

  return (
    <div className='flex flex-col'>
      <h2 className='heading-sm text-gray-800 mb-2'>Covered participants</h2>
      <div className='body text-gray-500 mb-4'>{subtitle}</div>
      <RenewalsConfirmComparisonTable
        prevPlanYear={currentPlanYear}
        currPlanYear={nextPlanYear}
        prevValues={prevValuesWithMetadata}
        currValues={currValuesWithMetadata}
      />
    </div>
  )
}
